import React from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';

/**
 * Module 2 — A2.7 Recruitment pipeline · prospect card.
 *
 * Pixel-faithful to mockup `module2-newcreator.jsx` M2_S7_Pipeline (lines 382-417):
 *   - container: padding 14, surface1, border, radius 12 — tappable to edit
 *   - top row: Avatar 32 + handle (13/600) + tiktok link line + status Chip (+ replied ✓)
 *   - divider → 3-col grid: Source / Forecast / Last F/U
 *   - optional support-needed footnote
 *
 * Status → accent color map (spec funnel):
 *   Prospect → textDim · Contacted → amber · Replied → cyan ·
 *   Negotiating → green · Onboarded → diamond · Declined → red
 *
 * Props:
 *   - prospect: recruitment_pipeline row (new A2.7 schema)
 *   - onClick?: (prospect) => void — opens the edit form
 */
export const PIPELINE_STATUS_COLOR = {
  prospect: TCN.textDim,
  contacted: TCN.amber,
  replied: TCN.cyan,
  negotiating: TCN.green,
  onboarded: TCN.diamond,
  declined: TCN.red,
};

const fmtMonth = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};
const fmtDay = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return v;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const linkText = (prospect, handle) => {
  if (prospect.tiktok_link) return prospect.tiktok_link.replace(/^https?:\/\//, '');
  return `tiktok.com/${handle}`;
};

const PipelineProspectCard = ({ prospect, onClick }) => {
  const { t } = useTranslation();
  const handle = prospect.tiktok_username?.startsWith('@')
    ? prospect.tiktok_username
    : `@${prospect.tiktok_username || '?'}`;
  const statusKey = (prospect.status || '').toLowerCase();
  const color = PIPELINE_STATUS_COLOR[statusKey] ?? TCN.textDim;

  const cell = (label, value) => (
    <div>
      <div style={{ color: TCN.textMute, fontSize: 10 }}>{label}</div>
      <div style={{ color: TCN.text, marginTop: 2 }} dir="auto">
        {value || '—'}
      </div>
    </div>
  );

  return (
    <div
      onClick={onClick ? () => onClick(prospect) : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick(prospect);
              }
            }
          : undefined
      }
      style={{
        padding: 14,
        background: TCN.surface1,
        border: `1px solid ${TCN.border}`,
        borderRadius: 12,
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Avatar name={handle} size={32} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: TCN.text }} dir="ltr">
            {handle}
          </div>
          <div
            style={{
              fontSize: 11,
              color: TCN.textMute,
              marginTop: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            dir="ltr"
          >
            <Icon name="link" size={10} color={TCN.textMute} />
            {linkText(prospect, handle)}
          </div>
        </div>
        {prospect.replied && (
          <span
            title={t('tcn.m2.pipeline.repliedTag', 'Replied')}
            style={{ display: 'inline-flex', alignItems: 'center', color: TCN.cyan }}
          >
            <Icon name="check" size={14} color={TCN.cyan} />
          </span>
        )}
        <Chip color={color} size="sm">
          {t(`tcn.m2.pipeline.status.${statusKey}`, statusKey.charAt(0).toUpperCase() + statusKey.slice(1))}
        </Chip>
      </div>

      <div
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: `1px solid ${TCN.border}`,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 8,
          fontSize: 11,
        }}
      >
        {cell(t('tcn.m2.pipeline.col.source', 'Source'), prospect.source)}
        {cell(t('tcn.m2.pipeline.col.forecast', 'Forecast'), fmtMonth(prospect.forecast_onboard_month))}
        {cell(t('tcn.m2.pipeline.col.lastFu', 'Last F/U'), fmtDay(prospect.last_follow_up_date))}
      </div>

      {prospect.support_needed && (
        <div style={{ marginTop: 8, fontSize: 11, color: TCN.textDim }} dir="auto">
          <span style={{ color: TCN.textMute }}>
            {t('tcn.m2.pipeline.col.support', 'Support')}:{' '}
          </span>
          {prospect.support_needed}
        </div>
      )}
    </div>
  );
};

export default PipelineProspectCard;
